/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('DOMContentLoaded', function() {
    var readMore = document.querySelector('.read_more_description');

    if(typeof readMore != 'undefined' && readMore != null) {
        var readMoreBtn = readMore.querySelector('.read_more_btn');

        readMoreBtn.addEventListener('click', function(e) {
            e.preventDefault();

            readMore.classList.toggle('open');

            if(readMore.classList.contains('open')) {
                readMoreBtn.innerText = 'Read less';
                readMoreBtn.setAttribute('aria-expanded', true);
            } else {
                readMoreBtn.innerText = 'Read more';
                readMoreBtn.setAttribute('aria-expanded', false);
            }
        });
    }
    
    var promoSlider = document.querySelector('.promo-block-slider');

    if(typeof promoSlider != 'undefined' && promoSlider != null) {
        var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        var intervalId;
        var currentIndex = 0;
        var slides = promoSlider.querySelectorAll('.promo-strip__container');
        var activeSlider = slides[currentIndex];
        var totalSlides = slides.length - 1;
        
        var nextBtn = promoSlider.querySelector('button.next');
        var prevBtn = promoSlider.querySelector('button.prev');

        if(typeof nextBtn != 'undefined' && nextBtn != null) {

            nextBtn.addEventListener('click', function(e) {
                e.preventDefault();
                clearInterval(intervalId);

                activeSlider.classList.remove('active');
                currentIndex++;
                if(currentIndex > totalSlides) {
                    currentIndex = 0;
                }
                activeSlider = slides[currentIndex];

                activeSlider.classList.add('active');
                if (!reduceMotion) { intervalId = setInterval(autoSlide, 10000); }
            });

            prevBtn.addEventListener('click', function(e) {
                e.preventDefault();
                clearInterval(intervalId);
                
                activeSlider.classList.remove('active');
                currentIndex--;
                if(currentIndex < 0) {
                    currentIndex = totalSlides;
                }
                activeSlider = slides[currentIndex];

                activeSlider.classList.add('active');
                if (!reduceMotion) { intervalId = setInterval(autoSlide, 10000); }
            });            
        } 
        
        if (!reduceMotion) { intervalId = setInterval(autoSlide, 10000); }

        // ADA fixes

        promoSlider.addEventListener('mouseenter', function () { clearInterval(intervalId); });

        promoSlider.addEventListener('focusin', function () { clearInterval(intervalId); });

        promoSlider.addEventListener('mouseleave', function () { if (!reduceMotion) intervalId = setInterval(autoSlide, 10000); });

        promoSlider.addEventListener('focusout', function () { if (!reduceMotion) intervalId = setInterval(autoSlide, 10000); });

        function autoSlide() {
            activeSlider.classList.remove('active');
            currentIndex++;
            if(currentIndex > totalSlides) {
                currentIndex = 0;
            }
            activeSlider = slides[currentIndex];

            activeSlider.classList.add('active');
        }
    }

    // Offers bar
    var openOffer = document.querySelector('.open-offers');

    if(typeof openOffer != 'undefined' && openOffer != null) {
        var showOffers = document.querySelector('#show-offers');
        var offerDrawer = document.querySelector('.js-offer-drawer');
        var titles = document.querySelector('#titles-discount');
        var titleJSON;
        if(typeof titles != 'undefined' && titles != null) {
            titleJSON = JSON.parse(titles.innerText);
        }
        var initialIndex = 0;

        // if(titleJSON.length > 0) {
        //     showOffers.innerText = titleJSON[0];
        // }
        
        openOffer.addEventListener('click', function(e) {
            e.preventDefault();
            offerDrawer.open(openOffer);
        });

        // voltage: sync aria-expanded to the drawer open/close state (ADA fix C)
        new MutationObserver(function () {
            openOffer.setAttribute('aria-expanded', offerDrawer.getAttribute('aria-hidden') === 'false');
        }).observe(offerDrawer, { attributes: true, attributeFilter: ['aria-hidden'] });

        // if(typeof showOffers != 'undefined' && showOffers != null) {
        //     var offerSlider = setInterval(function() {
        //         fadeTo(showOffers, 0, 300);            
        //         setTimeout(() => {
        //             showOffers.innerText = titleJSON[initialIndex];
        //             fadeTo(showOffers, 1.0, 500);
        //         }, 500);
        //         initialIndex++;
        //         if(initialIndex == titleJSON.length) {
        //             initialIndex = 0;
        //         }
        //     }, 8000);
        // }
    }

    //Accesibility JS

    const mobileMenuBtn = document.querySelector('.main-menu__toggle');

    if(typeof mobileMenuBtn != 'undefined' && mobileMenuBtn != null ) {
        mobileMenuBtn.addEventListener("click", function() {
          const ariaLabel = mobileMenuBtn.getAttribute("aria-label");
          console.log("click label " + ariaLabel);
          const labelSet = ariaLabel == 'Opening Menu' ? 'Closing Menu' : 'Opening Menu';
          setTimeout(function() {
            mobileMenuBtn.setAttribute("aria-label", labelSet);
          }, 500);
        });
    }

    // voltage: hide Style Number from cart line items
    function hideCartStyleNumber() {
      document.querySelectorAll('.cart-item__variants dt').forEach(function(dt) {
        if (dt.textContent.replace(':', '').trim() === 'Style Number') {
          dt.hidden = true;
          const next = dt.nextElementSibling;
          if (next && next.tagName === 'DD') next.hidden = true;
        }
      });
    }

    hideCartStyleNumber();
    document.addEventListener('on:cart:add', function() { setTimeout(hideCartStyleNumber, 50); });
    document.addEventListener('on:line-item:change', function() { setTimeout(hideCartStyleNumber, 50); });
    document.addEventListener('on:cart-drawer:after-open', hideCartStyleNumber);
});

// voltage: keep off-screen cart-recommendation slides out of the tab order (WCAG 2.4.3)
// Vendor snippet recommendations.liquid renders every card into the DOM; without this the
// cart drawer is 32 tab stops deep and Checkout is the last one.
(function () {
  function govern(scroller) {
    if (!scroller || scroller.dataset.voltageInertBound) return;
    scroller.dataset.voltageInertBound = '1';

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var li = entry.target;
        // never inert the subtree that currently holds focus — it would eject the user
        if (li.contains(document.activeElement)) { li.inert = false; return; }
        li.inert = !entry.isIntersecting;
      });
    }, { root: scroller, threshold: 0.5 });

    scroller.querySelectorAll('.slider__item').forEach(function (li) { io.observe(li); });
  }

  // the drawer re-renders on every cart change, so re-bind on mutation
  var drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  govern(drawer.querySelector('#cart-recommendations .slider'));
  var rearmTimer;
  new MutationObserver(function () {
    govern(drawer.querySelector('#cart-recommendations .slider'));
    // voltage: Samita injects its clone Checkout button AFTER the drawer's first auto-open,
    // so the vendor trapFocus snapshot from open time wraps Tab before reaching Checkout
    // (first-open skip, found by Matthew 2026-09-01 — same stale-snapshot disease as the
    // quickbuy fix). Re-arm the trap when drawer content changes while it's open and
    // holds focus; debounced because renders mutate in bursts.
    clearTimeout(rearmTimer);
    rearmTimer = setTimeout(function () {
      if (typeof trapFocus === 'function' && drawer.getAttribute('aria-hidden') === 'false' && drawer.contains(document.activeElement)) {
        trapFocus(drawer, document.activeElement);
      }
    }, 200);
  }).observe(drawer, { childList: true, subtree: true });
})();

// voltage: 2026-08-31 audit remediation — sitewide keyboard/motion fixes (findings 2, 3, 5i, 8, 9)
// One sweep, re-run on DOM mutation so lazily-instantiated components (deferred-media,
// app widgets) get equipped when they appear.
(function () {
  var reducedMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // finding 2 + 8: content carousels — inert off-screen slides (WCAG 2.4.3) and make the
  // scroll region keyboard-reachable (WCAG 2.1.1). Same governor as the cart drawer;
  // the voltageInertBound guard keeps the two bindings from overlapping.
  function governSlider(scroller, itemSel) {
    if (!scroller || scroller.dataset.voltageInertBound) return;
    scroller.dataset.voltageInertBound = '1';
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var li = entry.target;
        if (li.contains(document.activeElement)) { li.inert = false; return; }
        li.inert = !entry.isIntersecting;
      });
    }, { root: scroller, threshold: 0.5 });
    scroller.querySelectorAll(itemSel).forEach(function (li) { io.observe(li); });
  }

  // finding 3: autoplaying background videos — respect reduced-motion and add a pause
  // toggle (WCAG 2.2.2). Product videos have controls already and are skipped.
  function equipBgVideo(video) {
    if (video.dataset.voltagePauseBound) return;
    video.dataset.voltagePauseBound = '1';
    var host = video.closest('.video-section, [id*="background_video"]') || video.parentElement;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    if (reducedMotion) { try { video.autoplay = false; video.pause(); } catch (e) {} }
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'voltage-video-pause';
    function sync() {
      var playing = !video.paused;
      btn.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
      btn.textContent = playing ? '❚❚' : '▶';
    }
    btn.addEventListener('click', function () {
      if (video.paused) { video.play(); } else { video.pause(); }
    });
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    sync();
    host.appendChild(btn);
  }

  // finding 9: blog pagination arrows with visually-hidden-on-mobile labels
  function labelPagination() {
    var links = document.querySelectorAll('article-pagination .js-pagination-link');
    if (links.length && !links[0].hasAttribute('aria-label')) {
      links[0].setAttribute('aria-label', 'Previous post');
      links[links.length - 1].setAttribute('aria-label', 'Next post');
    }
  }

  function sweep() {
    document.querySelectorAll('.slider').forEach(function (s) {
      if (!s.dataset.voltageInertBound && s.scrollWidth > s.clientWidth + 1 && !s.hasAttribute('tabindex')) {
        s.setAttribute('tabindex', '0');
        if (!s.hasAttribute('role')) s.setAttribute('role', 'group');
        if (!s.hasAttribute('aria-label')) s.setAttribute('aria-label', 'Carousel');
      }
      governSlider(s, '.slider__item');
    });
    // finding 5 interim (app: Yotpo promoted-products) — remove when Yotpo fixes upstream
    document.querySelectorAll('#yotpo-promoted-products-widget .glide__track').forEach(function (t) {
      governSlider(t, '.glide__slide');
    });
    document.querySelectorAll('video[autoplay]:not([controls])').forEach(equipBgVideo);
    labelPagination();
  }

  sweep();
  new MutationObserver(sweep).observe(document.body, { childList: true, subtree: true });
})();

// voltage: quickbuy keyboard parity (WCAG 2.1.1) — vendor quick-add.js prefetches product
// details on the button's mouseenter only, so a keyboard-focused "Choose options" opens an
// empty pane. Mirror the prefetch on focus, reusing the vendor's own handler and its
// fetchedUrls dedupe. Delegated, so buttons injected by cart re-renders need no re-binding.
document.addEventListener('focusin', function (evt) {
  var btn = evt.target;
  if (!btn.matches || !btn.matches('.js-quick-add')) return;
  var qa = document.querySelector('quick-add-drawer');
  if (qa && typeof qa.handleQuickAddButtonMouseEnter === 'function') {
    qa.handleQuickAddButtonMouseEnter({ target: btn });
  }
});

// voltage: announce quickbuy loading state — the pane opens before its content arrives,
// leaving keyboard/SR users in an apparently empty dialog with no feedback
(function () {
  var qa = document.querySelector('quick-add-drawer');
  if (!qa) return;
  var status = document.createElement('div');
  status.className = 'visually-hidden';
  status.setAttribute('role', 'status');
  qa.appendChild(status);
  new MutationObserver(function () {
    var loading = qa.classList.contains('is-loading');
    if (loading) {
      status.textContent = 'Loading product options';
    } else if (status.textContent) {
      status.textContent = 'Product options loaded';
      // voltage: re-arm the vendor focus trap after the content renders. trapFocus()
      // (main.js:440) snapshots the pane's focusable elements once at open; a keyboard-first
      // open runs it while the pane is still empty, so Tab cycles only the back/close
      // buttons even after the options render. Rebuild the snapshot, keeping the user's
      // current focus position.
      if (typeof trapFocus === 'function' && qa.getAttribute('aria-hidden') === 'false') {
        var keep = qa.contains(document.activeElement) ? document.activeElement : qa;
        trapFocus(qa, keep);
      }
    }
  }).observe(qa, { attributes: true, attributeFilter: ['class'] });
})();

// voltage: after adding a cart upsell from the quickbuy pane, return to the cart drawer
// automatically — same action as the pane's back arrow. The vendor leaves the pane open
// with a 2s "View cart" alert that sits BEFORE the add-to-cart button in the DOM, so a
// keyboard user would have to Shift+Tab backwards to a link that disappears; landing in
// the cart drawer with the new item showing is clearer feedback for everyone. Scoped to
// the cart flow: the back arrow is only visible when the pane was opened FROM the cart,
// so collection-page quickshop keeps the vendor's stay-open behaviour. Listens for both
// events because a repeat add of a variant already in the cart fires on:line-item:change
// instead of on:cart:add on this theme.
(function () {
  function backToCartAfterAdd() {
    var qa = document.querySelector('quick-add-drawer');
    if (!qa || qa.getAttribute('aria-hidden') !== 'false') return;
    var back = qa.querySelector('.drawer__back-btn');
    if (!back || back.hidden) return;
    setTimeout(function () { back.click(); }, 150);
  }
  document.addEventListener('on:cart:add', backToCartAfterAdd);
  document.addEventListener('on:line-item:change', backToCartAfterAdd);
})();
